import LINKS from 'src/_helpers/links'
import { DEFAULT_JOURNAL_DOMAIN_ROUTES } from 'src/_helpers/journalDomainRoutes'
import { buildJournalArticleUrl } from 'src/_helpers/journals/buildJournalArticleUrl'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'

const safeString = (value) => (typeof value === 'string' ? value.trim() : '')

const withMainAppBase = (href, routeContext) => {
  if (!href || routeContext !== 'publisher-domain') return href

  const mainUrl = String(process.env.NEXT_PUBLIC_MAIN_URL || '').replace(/\/$/, '')

  return mainUrl ? `${mainUrl}${href.startsWith('/') ? href : `/${href}`}` : href
}

const getPublisherLabel = (hit, locale) => {
  const localizedTitles =
    hit?.publishersTitles?.[locale] ||
    hit?.publishersTitles?.en ||
    hit?.publishersTitles?.pl ||
    Object.values(hit?.publishersTitles || {}).find((value) => Array.isArray(value)) ||
    []

  if (Array.isArray(localizedTitles)) {
    const label = localizedTitles.map(safeString).filter(Boolean).join(', ')
    if (label) {
      return label
    }
  }

  return safeString(hit?.publisherTitle || hit?.publisher || hit?.publisher_name)
}

const buildCtaHref = (hit, locale, routeContext = 'main', journalRoutes = DEFAULT_JOURNAL_DOMAIN_ROUTES) => {
  const type = safeString(hit?.type)
  const slug = safeString(hit?.slugs?.[locale] || hit?.slug)
  const journalSlug = safeString(hit?.journalSlugs?.[locale] || hit?.journalSlug || hit?.journal_slug)
  const articleSlug = safeString(hit?.articleSlug || hit?.article_slug || slug)
  const monographSlug = safeString(hit?.monographSlugs?.[locale] || hit?.monographSlug || hit?.monograph_slug)

  if (routeContext === 'journal-domain') {
    if (type === 'journal') {
      return buildLocalizedPath(journalRoutes.HOME, locale)
    }
    if (type === 'journal_issue' && slug) {
      return buildLocalizedPath(journalRoutes.ISSUES.DETAIL, locale, { issue: slug })
    }
    if (type === 'journal_article' && articleSlug) {
      return buildJournalArticleUrl(hit, locale, 'journal-domain', journalRoutes)
    }
    if (type === 'journal_page' && slug) {
      return buildLocalizedPath(journalRoutes.JOURNALS.SUBPAGE, locale, { slug })
    }
  }

  if (type === 'journal' && slug) {
    return withMainAppBase(LINKS.JOURNALS.DETAIL[locale].replace(':slug', slug), routeContext)
  }
  if (type === 'journal_issue' && journalSlug && slug) {
    return withMainAppBase(
      LINKS.ISSUES.DETAIL[locale].replace(':slug', journalSlug).replace(':issue', slug),
      routeContext,
    )
  }
  if (type === 'journal_article' && journalSlug && articleSlug) {
    return withMainAppBase(buildJournalArticleUrl(hit, locale), routeContext)
  }
  if (type === 'journal_page' && journalSlug && slug) {
    return withMainAppBase(
      LINKS.JOURNALS.SUBPAGE[locale].replace(':journal', journalSlug).replace(':slug', slug),
      routeContext,
    )
  }
  if ((type === 'monographs' || type === 'monograph') && slug) {
    return withMainAppBase(LINKS.MONOGRAPHS.DETAIL[locale].replace(':slug', slug), routeContext)
  }
  if (type === 'monograph_chapter' && monographSlug && slug) {
    return withMainAppBase(
      LINKS.MONOGRAPHS.CHAPTER[locale].replace(':slug', monographSlug).replace(':chapter', slug),
      routeContext,
    )
  }

  return null
}

const buildImageSrc = (hit) => {
  const thumbPath = hit?.thumbs?.list?.url
  const thumbVersion = hit?.thumbs?.list?.version
  if (thumbPath && typeof thumbPath === 'string' && thumbPath.startsWith('/')) {
    return getMediaUrl(thumbPath, typeof thumbVersion === 'number' ? thumbVersion : 1)
  }
}

const buildMetadata = (hit, locale) => {
  const metadata = []
  const issn = safeString(hit?.issn)
  const eIssn = safeString(hit?.eIssn || hit?.e_issn)
  const isbns = Array.isArray(hit?.isbns) ? hit.isbns.map(safeString).filter(Boolean) : []
  const legacyIsbn = safeString(hit?.isbn)
  const legacyEIsbn = safeString(hit?.eIsbn || hit?.e_isbn)
  const doi = safeString(hit?.doi)
  const publisher = getPublisherLabel(hit, locale)
  const year = hit?.year

  if (hit?.type !== 'monographs' && (issn || eIssn)) {
    const parts = []
    if (issn) parts.push(`ISSN ${issn}`)
    if (eIssn) parts.push(`eISSN ${eIssn}`)
    metadata.push(parts.join(', '))
  }
  if (isbns.length > 0) metadata.push(`ISBN ${isbns.join(', ')}`)
  else {
    if (legacyIsbn) metadata.push(`ISBN ${legacyIsbn}`)
    if (legacyEIsbn) metadata.push(`eISBN ${legacyEIsbn}`)
  }
  if (doi) metadata.push(`DOI ${doi}`)
  if (publisher) metadata.push(publisher)
  if (year) metadata.push(String(year))

  return metadata.slice(0, 3)
}

const buildFooterLines = (hit) => {
  const authors = hit?.authors

  if (typeof authors === 'string') {
    const trimmed = authors.trim()
    return trimmed ? [trimmed] : []
  }

  if (Array.isArray(authors)) {
    const names = authors
      .map((author) => {
        if (typeof author === 'string') return author.trim()
        if (!author || typeof author !== 'object') return ''
        return safeString(author.fullName) || ''
      })
      .filter(Boolean)

    return names.length ? [names.join(', ')] : []
  }

  return []
}

export const meiliHitToProductTile = (
  hit,
  locale,
  {
    prependTags = [],
    includeTypeTag = false,
    includeOpenAccessTag = false,
    includePreviewTag = false,
    routeContext = 'main',
    journalRoutes = DEFAULT_JOURNAL_DOMAIN_ROUTES,
  } = {},
) => {
  const type = safeString(hit?.type)
  if (type === 'author') return null

  const title = safeString(hit?.title || hit?.name)
  if (!title) return null

  const tags = [...prependTags]
  if (includeTypeTag && type) tags.push({ label: type, type: 'default' })
  if (includeOpenAccessTag && hit?.openAccess) tags.push({ label: 'Open Access', type: 'open-access' })
  if (hit?.aheadOfPrint) tags.push({ label: 'Ahead of print', type: 'default' })
  if (hit?.novelty) tags.push({ labelKey: 'T_NOVELTY', label: 'Nowość', type: 'new' })
  if (includePreviewTag && hit?.preview) tags.push({ labelKey: 'T_PREVIEW', label: 'Zapowiedź', type: 'preview' })

  const subtitle =
    type === 'monograph_chapter'
      ? safeString(
          hit?.translations?.[locale]?.bookTitle ||
            hit?.translations?.pl?.bookTitle ||
            hit?.translations?.en?.bookTitle,
        )
      : safeString(hit?.subtitle)

  return {
    id: hit?.id || hit?.uuid || title,
    title,
    subtitle,
    imageSrc: buildImageSrc(hit),
    imageAlt: title,
    metadata: buildMetadata(hit, locale),
    footerLines: buildFooterLines(hit),
    tags,
    ctaHref: buildCtaHref(hit, locale, routeContext, journalRoutes),
    isArticle: type === 'journal_article' || type === 'monograph_chapter',
  }
}
